import { useSlate } from "slate-react";
import { Editor, Element as SlateElement, Transforms } from "slate";
import { IconButton, Tooltip } from "@mui/material";
import { useCallback } from "react";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import {
  AlignType,
  BlockButtonProps,
  CustomEditor,
  CustomElementFormat,
  ListType,
  TEXT_ALIGN_TYPES,
} from "./RichTextEditor.types";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LooksOneOutlinedIcon from "@mui/icons-material/LooksOneOutlined";
import LooksTwoOutlinedIcon from "@mui/icons-material/LooksTwoOutlined";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { isAlignElement } from "./Element";

const LIST_TYPES = ["numbered-list", "bulleted-list"];

export const BlockButton: React.FC<BlockButtonProps> = ({ format }) => {
  const editor = useSlate();
  const isAlignType = (format: CustomElementFormat): format is AlignType => {
    return TEXT_ALIGN_TYPES.includes(format as AlignType);
  };

  const isListType = (format: CustomElementFormat): format is ListType => {
    return LIST_TYPES.includes(format as ListType);
  };

  const toggleBlock = (editor: CustomEditor, format: CustomElementFormat) => {
    const isActive = isBlockActive(editor, format);
    const isList = isListType(format);

    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        isListType(n.type) &&
        !isAlignType(format),
      split: true,
    });
    let newProperties: Partial<SlateElement>;
    if (isAlignType(format)) {
      newProperties = {
        align: isActive ? undefined : format,
      };
    } else {
      newProperties = {
        type: isActive ? "paragraph" : isList ? "list-item" : format,
      };
    }
    Transforms.setNodes<SlateElement>(editor, newProperties);

    if (!isActive && isList) {
      const block = { type: format, children: [] };
      Transforms.wrapNodes(editor, block);
    }
  };

  const isBlockActive = (
    editor: CustomEditor,
    format: CustomElementFormat,
    blockType: "type" | "align" = "type"
  ) => {
    const { selection } = editor;
    if (!selection) return false;

    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n) => {
          if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
            if (blockType === "align" && isAlignElement(n)) {
              return n.align === format;
            }
            return n.type === format;
          }
          return false;
        },
      })
    );

    return !!match;
  };

  const getButtonIcon = useCallback((title: string) => {
    switch (title) {
      case "block-quote":
        return <FormatQuoteIcon fontSize="small" />;
      case "heading-one":
        return <LooksOneOutlinedIcon fontSize="small" />;
      case "heading-two":
        return <LooksTwoOutlinedIcon fontSize="small" />;
      case "numbered-list":
        return <FormatListNumberedIcon fontSize="small" />;
      case "bulleted-list":
        return <FormatListBulletedIcon fontSize="small" />;
      case "left":
        return <FormatAlignLeftIcon fontSize="small" />;
      case "center":
        return <FormatAlignCenterIcon fontSize="small" />;
      case "right":
        return <FormatAlignRightIcon fontSize="small" />;
      case "align":
        return <FormatAlignJustifyIcon fontSize="small" />;
    }
  }, []);

  return (
    <Tooltip title={format.charAt(0).toUpperCase() + format.slice(1)}>
      <IconButton
        onMouseDown={(event) => {
          event.preventDefault();
          toggleBlock(editor, format);
        }}
        color={isBlockActive(editor, format) ? "primary" : "default"}
      >
        {getButtonIcon(format)}
      </IconButton>
    </Tooltip>
  );
};
